/**
 * MODULE: _shared/frontend-shell/src/layout/shellAssistant/useShellAssistantRailWidth.ts
 * PURPOSE: Persisted + draggable width for the right assistant rail.
 * RECENT: 2026-06-18 [2.2.1450] — localStorage-backed rail width; pointer drag on desktop split.
 */

import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import {
  clampShellAssistantRailWidthPx,
  defaultShellAssistantRailWidthPx,
  SHELL_ASSISTANT_RAIL_WIDTH_STORAGE_KEY,
} from './shellAssistantLayout';

function viewportWidth(): number {
  return typeof window !== 'undefined' ? window.innerWidth : 1280;
}

function readStoredWidth(isMobile: boolean): number {
  const vw = viewportWidth();
  if (typeof window === 'undefined') return defaultShellAssistantRailWidthPx(isMobile, vw);
  try {
    const raw = window.sessionStorage.getItem(SHELL_ASSISTANT_RAIL_WIDTH_STORAGE_KEY);
    const n = raw ? Number.parseInt(raw, 10) : NaN;
    if (Number.isFinite(n)) return clampShellAssistantRailWidthPx(n, isMobile, vw);
  } catch {
    /* ignore */
  }
  return defaultShellAssistantRailWidthPx(isMobile, vw);
}

export function useShellAssistantRailWidth(isMobile: boolean) {
  const [railWidthPx, setRailWidthPx] = useState(() => readStoredWidth(isMobile));
  const dragRef = useRef<{ startX: number; startW: number } | null>(null);

  useEffect(() => {
    setRailWidthPx(readStoredWidth(isMobile));
  }, [isMobile]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => {
      setRailWidthPx((current) =>
        clampShellAssistantRailWidthPx(current, isMobile, window.innerWidth)
      );
    };
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, [isMobile]);

  const persistWidth = useCallback(
    (px: number) => {
      const clamped = clampShellAssistantRailWidthPx(px, isMobile, viewportWidth());
      setRailWidthPx(clamped);
      try {
        window.sessionStorage.setItem(SHELL_ASSISTANT_RAIL_WIDTH_STORAGE_KEY, String(clamped));
      } catch {
        /* ignore */
      }
    },
    [isMobile]
  );

  const onResizePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      dragRef.current = { startX: event.clientX, startW: railWidthPx };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [railWidthPx]
  );

  const onResizePointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag) return;
      const delta = drag.startX - event.clientX;
      persistWidth(drag.startW + delta);
    },
    [persistWidth]
  );

  const onResizePointerEnd = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  return {
    railWidthPx,
    onResizePointerDown,
    onResizePointerMove,
    onResizePointerEnd,
  };
}
